import type { NoteView, ReferenceOption } from "@fieldpath/shared";
import { mdyTime } from "@fieldpath/shared";
import { useEffect, useRef, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { imageFiles, refusal, shrink, toBase64, type PendingImage } from "../noteImages";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { MentionBox, mentionsInText } from "./MentionBox";

interface Props {
  fetcher: Fetcher;
  taskId: string;
  people: ReferenceOption[];
}

/**
 * Notes people write on a task, newest first.
 *
 * Append-only, and it says so. The two ledgers above it are written by the
 * system — the stage path, and why dates moved — this is the one people write
 * in, for the things that do not fit either: what the client said on the phone,
 * why an approach changed.
 */
export function TaskNotes({ fetcher, taskId, people }: Props) {
  const [notes, setNotes] = useState<NoteView[] | null>(null);
  const [body, setBody] = useState("");
  const [mentions, setMentions] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Images waiting to go with the note (3 Nov 2026), and how many are still
  // being shrunk -- counted against the five, and Post waits for them.
  const [pending, setPending] = useState<PendingImage[]>([]);
  const [shrinking, setShrinking] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  // Thumbnails are object URLs; let them go when the page does.
  useEffect(() => () => pendingRef.current.forEach((i) => URL.revokeObjectURL(i.url)), []);

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void api
      .listNotes(fetcher, taskId)
      .then(setNotes)
      .catch((e: Error) => setError(e.message));
  }, [fetcher, taskId, back]);

  /** Pasted, dropped or picked: each checked, shrunk, and put in the box. */
  const addFiles = async (files: readonly File[]) => {
    setError(null);
    let count = pending.length + shrinking;
    for (const file of files) {
      const refused = refusal(file, count);
      if (refused) {
        setError(refused);
        break;
      }
      count += 1;
      setShrinking((n) => n + 1);
      try {
        const { contentType, blob } = await shrink(file);
        const image: PendingImage = { key: crypto.randomUUID(), contentType, blob, url: URL.createObjectURL(blob) };
        setPending((current) => [...current, image]);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setShrinking((n) => n - 1);
      }
    }
  };

  const removeImage = (key: string) =>
    setPending((current) => {
      current.filter((i) => i.key === key).forEach((i) => URL.revokeObjectURL(i.url));
      return current.filter((i) => i.key !== key);
    });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const images = await Promise.all(
        pending.map(async (i) => ({ contentType: i.contentType, data: await toBase64(i.blob) })),
      );
      setNotes(await api.addNote(fetcher, taskId, body, willNotify, images));
      setBody("");
      setMentions([]);
      pending.forEach((i) => URL.revokeObjectURL(i.url));
      setPending([]);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  // Typed into the note, plus anyone ticked in the list. A union rather than
  // one or the other: the text is the usual way, the list is there for a name
  // somebody cannot spell.
  const typed = mentionsInText(body, people);
  const willNotify = [...new Set([...typed, ...mentions])];

  const toggleMention = (key: string) =>
    setMentions((current) =>
      current.includes(key) ? current.filter((k) => k !== key) : [...current, key],
    );

  return (
    <div className="card">
      <h2>Notes</h2>
      <p className="muted" style={{ marginTop: -8 }}>
        A running record. Notes cannot be edited once posted.
      </p>

      {error ? <div className="error">{error}</div> : null}

      <form onSubmit={submit} style={{ marginBottom: 18 }}>
        <div
          className="field"
          // A screenshot pasted or dropped into the box goes with the note.
          // A paste that also carries TEXT is left to the box: copying from
          // Word or a web page puts a picture of the text on the clipboard
          // too, and that is not an image anybody meant to attach.
          onPaste={(e) => {
            if (e.clipboardData.types.includes("text/plain")) return;
            const files = imageFiles(e.clipboardData.items);
            if (files.length === 0) return;
            e.preventDefault();
            void addFiles(files);
          }}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes("Files")) e.preventDefault();
          }}
          onDrop={(e) => {
            const files = imageFiles(e.dataTransfer.files);
            if (files.length === 0) return;
            e.preventDefault();
            void addFiles(files);
          }}
        >
          <MentionBox
            value={body}
            people={people}
            onChange={setBody}
            placeholder="What happened, what was decided, what someone needs to know. Type @ to tag someone. Paste or drop a screenshot here."
          />
        </div>

        {pending.length > 0 || shrinking > 0 ? (
          <div className="note-images" style={{ marginBottom: 10 }}>
            {pending.map((i) => (
              <span key={i.key} className="note-image pending">
                <img src={i.url} alt="" />
                <button type="button" className="note-image-remove" aria-label="Remove image" onClick={() => removeImage(i.key)}>
                  ✕
                </button>
              </span>
            ))}
            {shrinking > 0 ? <span className="muted" style={{ fontSize: 13 }}>…</span> : null}
          </div>
        ) : null}

        {/* What the note will actually do, stated before it is posted.
            Mentions come from the text itself, so this is a readout rather
            than a second place to manage them. */}
        {willNotify.length > 0 ? (
          <p className="muted" style={{ margin: "0 0 10px" }}>
            Will notify{" "}
            <strong>
              {willNotify
                .map((k) => people.find((p) => p.key === k)?.label ?? k)
                .join(", ")}
            </strong>
          </p>
        ) : null}

        <details style={{ marginBottom: 10 }}>
          <summary className="muted" style={{ cursor: "pointer", fontSize: 13 }}>
            {/* Says what it is for (punch list #55, 20 Oct 2026); it read
                "Or pick from a list". */}
            @ Mention someone from a list{mentions.length > 0 ? ` (${mentions.length})` : ""}
          </summary>
          <div
            style={{
              maxHeight: 150,
              overflowY: "auto",
              border: "1px solid var(--line)",
              borderRadius: 6,
              padding: "8px 10px",
              marginTop: 6,
            }}
          >
            {people.map((p) => (
              <label
                key={p.key}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 4,
                  fontSize: 14,
                  color: "var(--ink)",
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: "auto" }}
                  checked={mentions.includes(p.key)}
                  onChange={() => toggleMention(p.key)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </details>

        {/* Bottom right, under the field it submits — the end of the action
            rather than the start of the next thing. */}
        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          {/* Picks from files -- on a phone, its photos or the camera. */}
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              void addFiles(imageFiles(e.target.files));
              e.target.value = "";
            }}
          />
          <button type="button" onClick={() => fileInput.current?.click()} disabled={saving}>
            Add image
          </button>
          <button
            className="primary"
            type="submit"
            disabled={saving || shrinking > 0 || (body.trim() === "" && pending.length === 0)}
          >
            {saving ? "Posting…" : "Post note"}
          </button>
        </div>
      </form>

      {notes === null ? (
        <p className="muted">Loading…</p>
      ) : notes.length === 0 ? (
        <p className="muted">No notes yet.</p>
      ) : (
        <ul className="history" style={{ listStyle: "none", padding: 0 }}>
          {notes.map((n) => (
            <li
              key={n.id}
              style={{
                borderTop: "1px solid var(--line)",
                paddingTop: 10,
                marginBottom: 10,
              }}
            >
              <div className="row spread">
                <strong>{n.authorName ?? "Unknown author"}</strong>
                <span className="muted" style={{ fontSize: 12 }}>
                  {mdyTime(n.createdAt)}
                </span>
              </div>
              {/* Preserve the line breaks someone typed; a note is often a
                  short list rather than a paragraph. */}
              {n.body !== "" ? <p style={{ whiteSpace: "pre-wrap", margin: "4px 0 6px" }}>{n.body}</p> : null}
              {n.images.length > 0 ? (
                <div className="note-images" style={{ margin: "6px 0" }}>
                  {n.images.map((i) => (
                    <NoteImage key={i.id} fetcher={fetcher} taskId={taskId} imageId={i.id} />
                  ))}
                </div>
              ) : null}
              {n.mentions.length > 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>
                  Notified: {n.mentions.map((m) => m.label).join(", ")}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * One posted image: fetched with the sign-in (an <img> cannot send it), shown
 * small, and full size over the page on a click.
 */
function NoteImage({ fetcher, taskId, imageId }: { fetcher: Fetcher; taskId: string; imageId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let made: string | null = null;
    let live = true;
    api
      .noteImage(fetcher, taskId, imageId)
      .then((blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [fetcher, taskId, imageId]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (failed) return <span className="note-image muted">Image could not be loaded</span>;
  if (!url) return <span className="note-image loading" />;
  return (
    <>
      <button type="button" className="note-image" onClick={() => setOpen(true)} aria-label="Open image full size">
        <img src={url} alt="" />
      </button>
      {open ? (
        <div className="modal-backdrop note-image-full" onMouseDown={() => setOpen(false)} role="dialog" aria-modal="true">
          <img src={url} alt="" />
        </div>
      ) : null}
    </>
  );
}
